<template>
  <div class="mixers-page layout-view">
    <div class="layout-view__controls">
      <div class="page-header flex space-between w-full mb-3">
        <h1 class="text-display ml-4">Міксери</h1>
        <div>
          <v-btn class="ml-4" medium @click="showAddMixer = true" :disabled="isLoadingMixers">
            <v-icon position-left font-size="1" icon="plus"></v-icon>
            <span>{{ 'base.add_mixer' | translate }}</span>
          </v-btn>
        </div>
      </div>
    </div>

    <div v-if="isLoadingMixers" class="loading-container empty-state">
      <span>Завантаження міксерів...</span>
    </div>

    <div v-else-if="mixersError" class="error-state">
      <p>Помилка завантаження: {{ mixersError.message }}</p>
      <v-btn @click="loadMixersData">Спробувати знову</v-btn>
    </div>

    <div v-else-if="mixersCount === 0" class="empty-state">
      <p>Немає міксерів</p>
    </div>

    <div v-else class="mixers-grid list">
      <v-card v-for="mixer in allMixers" :key="mixer.id" class="mixer-card v-card">
        <div class="mixer-header item flex space-between align-center">
          <h2 @click="goToModels(mixer.id)"
            class="mixer-name card-title cursor-pointer hover--underline hover--primary">
            {{ mixer.name }}
          </h2>
          <v-btn class="v-btn--icon v-btn--default" medium @click="showEditMixerModal(mixer)">
            <v-icon font-size="1.3" icon="cog"></v-icon>
          </v-btn>
        </div>
      </v-card>
    </div>

    <v-dialog max-width="330" v-model="showAddMixer">
      <AddMixerModal @close="showAddMixer = false" @create="handleMixerAdded" @update="handleMixerAdded" />
    </v-dialog>

    <v-dialog max-width="330" v-model="showEditMixer">
      <EditMixerModal :selected="selectedMixer" @close="showEditMixer = false" 
        @update="handleMixerUpdated" @delete="handleMixerDeleted" />
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AddMixerModal from './modals/AddMixerModal.vue'
import EditMixerModal from './modals/EditMixerModal.vue'

export default {
  name: 'MixersView',
  components: { AddMixerModal, EditMixerModal },

  data () {
    return {
      showAddMixer: false,
      showEditMixer: false,
      selectedMixer: null
    }
  },

  computed: {
    ...mapGetters('baseMixers', [
      'allMixers',
      'mixersCount',
      'isLoadingMixers',
      'mixersError'
    ])
  },

  async mounted () {
    if (this.mixersCount === 0 && !this.isLoadingMixers) {
      await this.loadMixersData()
    }
  },

  methods: {
    ...mapActions('baseMixers', [
      'loadMixers',
      'createMixer',
      'updateMixer',
      'deleteMixer',
      'resetState'
    ]),

    async loadMixersData () {
      try {
        await this.loadMixers()
      } catch (error) {
        console.error('Error loading mixers:', error)
        this.$toasted.error('Не вдалося завантажити міксери')
      }
    },

    goToModels (mixerId) {
      this.$router.push({
        name: 'MixerModels',
        params: { mixerId: String(mixerId) }
      })
    },

    showEditMixerModal (mixer) {
      this.selectedMixer = { ...mixer }
      this.showEditMixer = true
    },

    async handleMixerAdded (mixerData) {
      try {
        await this.createMixer(mixerData)
        this.showAddMixer = false
        this.$toasted.success('Міксер успішно додано')
      } catch (error) {
        console.error('Error creating mixer:', error)
        this.$toasted.error('Не вдалося створити міксер')
      }
    },

    async handleMixerUpdated (updatedMixerData) {
      try {
        const result = await this.updateMixer({
          id: updatedMixerData.id,
          data: updatedMixerData
        })
        this.$store.commit('baseMixers/SET_CURRENT_MIXER', result)
        this.showEditMixer = false
        this.$toasted.success('Міксер успішно оновлено')
      } catch (error) {
        console.error('Error updating mixer:', error)
        this.$toasted.error('Не вдалося оновити міксер')
      }
    },

    async handleMixerDeleted (mixerId) {
      try {
        await this.deleteMixer(mixerId)
        this.showEditMixer = false
        this.$toasted.success('Міксер успішно видалено')
      } catch (error) {
        console.error('Error deleting mixer:', error)
        this.$toasted.error('Не вдалося видалити міксер')
      }
    }
  },

  beforeDestroy () {
    this.resetState()
  }
}
</script>

<style lang="scss" scoped>
  @import '@/sass/abstracts/_mixins.scss';
.list {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  column-gap: 1.5rem;
  row-gap: 1.5rem;
    @include tablet {
       grid-template-columns: 1fr 1fr;
    }
    @include mobile {
       grid-template-columns: 1fr;
    }
}

.item {
  padding: 1.5rem 0.5rem 1.5rem 0;
  min-height: 8rem;
}

.mixer-name {
  padding-right: .5rem;
  padding-left: 2.5rem;
  border-left: .4rem solid #1da643;
  min-height: 4.3rem;
  display: flex;
  align-items: center;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
}

.empty-state {
  margin-top: 3.4rem;
  text-align: center;
  padding: 2rem;
  color: #666;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.error-state {
  text-align: center;
  padding: 2rem;
  color: #d32f2f;
}
</style>